import { PrimaryButton, SecondaryButton } from '@curie/react'
import { Button, cn, Popover, PopoverContent, PopoverTrigger } from '@nextui-org/react'
import { createTwoFilesPatch } from 'diff'
import JSZip from 'jszip'
import React, { useState } from 'react'
import { PopoverNavDownIcon } from '~/components/icons'
import { useConversationMetadata, useConversationUuid, useTaskStatus } from '~/hooks'
import { AgentTaskStatus, CommandType, commandTypeToJSON, Diff, DiffType } from '~/protos/agents'
import { requestChanges } from '~/services/taskService'
import { BetterToast } from '~/utils/toast'
import { cm } from '~/utils/utils'
import { SimpleTextarea } from '../../SimpleTextarea'
import { useGeneralReviewComment, useReviewDiffCommentsWithRef } from './hooks'
import { ReviewHistory } from './ReviewHistory'

interface FileListProps {
  diffs: Diff[] | null
  activeDiff: Diff | null
  onSelectDiff: (diff: Diff) => void
  onAcceptDiff: () => void
  readOnly?: boolean
  fileListsOnly?: boolean
}

const FileList: React.FC<FileListProps> = ({
  diffs = null,
  activeDiff,
  onSelectDiff,
  onAcceptDiff,
  readOnly = false,
  fileListsOnly = false
}) => {
  const [disabled, setIsDisabled] = React.useState<boolean>(false)
  const taskStatus = useTaskStatus()
  const isDisabled = disabled || taskStatus !== AgentTaskStatus.DIFF_GENERATED
  const getLabel = (diffType: DiffType) => {
    switch (diffType) {
      case DiffType.MODIFY:
        return 'M'
      case DiffType.ADD:
        return 'U'
      case DiffType.DELETE:
        return 'D'
      default:
        return ''
    }
  }

  const getFilePath = (diff: Diff | null) => {
    if (diff == null) return ''
    return diff.diffType === DiffType.DELETE ? diff.aFilePath : diff.bFilePath
  }

  const renderLabel = (diff: Diff) => {
    const label = getLabel(diff.diffType)
    switch (diff.diffType) {
      case DiffType.ADD:
        return <span className='px-1 font-semibold text-[#2d8646]'>{label}</span>
      case DiffType.MODIFY:
        return <span className='px-1 font-semibold text-[#eb5325]'>{label}</span>
      case DiffType.DELETE:
        return <span className='px-1 font-semibold text-[#df2a0f]'>{label}</span>
    }
  }

  const handleRequestChanges = async () => {
    if (comment.length === 0 && comments.length === 0) {
      BetterToast.error('Comment is required')
      return
    }
    setIsDisabled(true)
    await requestChanges({
      uuid,
      diffReview: {
        generalDiffComment: comment,
        diffComments: comments
      }
    })
    BetterToast.success('Requested changes submitted')
  }

  const handleDownloadDiffPatch = async () => {
    const generateCombinedPatch = (diffs: Diff[]) =>
      diffs
        .map(diff =>
          createTwoFilesPatch(
            diff.aFilePath || '/dev/null',
            diff.bFilePath || '/dev/null',
            (diff.aFileContent || '').trimEnd(),
            (diff.bFileContent || '').trimEnd()
          )
        )
        .join('\n')
        .trimEnd() + '\n'

    if (!diffs) {
      BetterToast.error('No diffs to download')
      return
    }

    try {
      const patch = generateCombinedPatch(diffs)
      const blob = new Blob([patch], { type: 'text/plain' })
      const url = window.URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = 'diff.patch'
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      window.URL.revokeObjectURL(url)
    } catch (error) {
      console.error('Error creating patch file:', error)
      BetterToast.error('Failed to create patch file')
    }
  }

  const handleDownloadZip = async () => {
    try {
      const zip = new JSZip()
      const folderA = zip.folder('original')
      const folderB = zip.folder('generated')

      const addFileToZip = (filePath: string, fileContent: string, targetFolder: JSZip | null) => {
        if (!targetFolder) return

        const pathSegments = filePath.split('/')
        const fileName = pathSegments.pop()
        let currentFolder = targetFolder

        pathSegments.forEach(segment => {
          currentFolder = currentFolder.folder(segment) ?? currentFolder
        })

        if (fileName && fileContent) {
          currentFolder.file(fileName, fileContent)
        }
      }

      diffs?.forEach(diff => {
        addFileToZip(diff.aFilePath, diff.aFileContent, folderA)
        addFileToZip(diff.bFilePath, diff.bFileContent, folderB)
      })

      const content = await zip.generateAsync({ type: 'blob' })
      const url = window.URL.createObjectURL(content)
      const a = document.createElement('a')
      a.href = url
      a.download = 'diffs.zip'
      a.click()
      window.URL.revokeObjectURL(url)

      BetterToast.success('Zip file downloaded successfully!')
    } catch (error) {
      console.error('Error creating zip file:', error)
      BetterToast.error('Failed to create zip file')
    }
  }

  const handleAcceptDiff = async () => {
    setIsDisabled(true)
    await onAcceptDiff()
  }
  const uuid = useConversationUuid()
  const [comment, setComment] = useGeneralReviewComment()
  const [comments] = useReviewDiffCommentsWithRef()

  const DownloadPopover = () => {
    const [isOpen, setIsOpen] = useState(false)
    return (
      <div className='flex w-full justify-end mb-4 pr-8 caret-transparent'>
        <Popover
          placement='bottom-end'
          radius='lg'
          offset={4}
          isOpen={isOpen}
          onOpenChange={setIsOpen}
          style={{
            zIndex: 1000000 // Note that the z-index of the BaseModal is 999999 (6 * 9)
          }}
          classNames={{
            base: 'min-w-[280px] text-black',
            content: 'p-0 rounded-2xl shadow-lg',
            trigger: 'group'
          }}
        >
          <PopoverTrigger>
            <Button
              size='lg'
              radius='lg'
              className={`text-black px-4 py-2 flex items-center gap-1 ${
                isOpen ? 'bg-gray-300' : 'bg-white'
              }`}
              disableAnimation={true}
            >
              Download Options
              <PopoverNavDownIcon />
            </Button>
          </PopoverTrigger>
          <PopoverContent>
            <div className='flex flex-col w-full py-2'>
              <button
                className='w-full px-4 py-3 text-left hover:bg-gray-50 flex items-center justify-between border-b border-gray-100'
                onClick={handleDownloadZip}
              >
                <span className='text-base'>Download Generated Code</span>
              </button>
              <button
                className='w-full px-4 py-3 text-left hover:bg-gray-50 flex items-center justify-between'
                onClick={handleDownloadDiffPatch}
              >
                <span className='text-base'>Download Diff Patch</span>
              </button>
            </div>
          </PopoverContent>
        </Popover>
      </div>
    )
  }

  const { mode } = useConversationMetadata()
  const isCodeEnhancer = mode === commandTypeToJSON(CommandType.GENERATE_SIMPLE_CODE_PR)
  const shouldDisableCodeReview = isCodeEnhancer ? false : true

  return (
    <div className='border-r-1 border-outline min-w-[200px] relative'>
      {Array.isArray(diffs) ? (
        <>
          <div className='flex flex-col'>
            {!readOnly && !fileListsOnly && (
              <div className='headline-lg-bold mt-16 ml-16 mb-2'>Editor</div>
            )}
            <ul className={cn(`w-2/3 ${fileListsOnly ? 'mx-8 mb-3' : 'ml-16 pb-4 pr-16'}`)}>
              {diffs.map((diff, index) => (
                <li
                  key={index}
                  onClick={() => onSelectDiff(diff)}
                  className={cm(
                    'rounded-sm text-default-600 items-center p-1 cursor-pointer overflow-hidden',
                    'text-ellipsis hover:bg-[#f0f0f0]',
                    getFilePath(diff) == getFilePath(activeDiff) ? 'bg-[#ededed]' : ''
                  )}
                >
                  {renderLabel(diff)}
                  {getFilePath(diff)}
                </li>
              ))}
            </ul>
            <DownloadPopover />
            {!readOnly && !fileListsOnly && (
              <div className='flex flex-row ml-16 w-full'>
                {shouldDisableCodeReview ? null : (
                  <div className='h-full mr-6'>
                    <SimpleTextarea
                      minRows={4}
                      placeholder='Leave a general comment...'
                      value={comment}
                      onChange={value => setComment(uuid, value)}
                      className='w-[328px]'
                    />
                  </div>
                )}

                <div className='flex flex-col mr-8'>
                  <PrimaryButton
                    className='mb-2 bg-light-success text-white py-3 !border-none'
                    onClick={handleAcceptDiff}
                    isDisabled={isDisabled}
                  >
                    Approve
                  </PrimaryButton>
                  {shouldDisableCodeReview ? null : (
                    <SecondaryButton
                      onClick={handleRequestChanges}
                      isDisabled={isDisabled}
                      className='bg-light-error text-white h-9 py-3 !border-none'
                    >
                      Request Changes
                    </SecondaryButton>
                  )}
                </div>
                {shouldDisableCodeReview ? null : (
                  <div className=''>
                    <ReviewHistory title='Review History'></ReviewHistory>
                  </div>
                )}
              </div>
            )}
          </div>
        </>
      ) : (
        <div className='empty p-2 text-default-500'>Loading files...</div>
      )}
    </div>
  )
}

export default FileList
